import axios from "axios";

/**
 * Which API the web portal talks to.
 *
 * The mobile app has to juggle 10.0.2.2 / localhost per platform; the browser
 * always reaches the machine it is running on, so one host is enough. Point
 * REACT_APP_API_HOST at the deployed server for production builds.
 */
/**
 * Pinned to the local server. To point a build somewhere else, change this line
 * (or swap in `process.env.REACT_APP_API_HOST || DEFAULT_HOST` and set the var
 * at build time):
 *
 *   deployed: https://kaamwala-server.vercel.app
 */
export const SERVER_URL =
  "https://getkaamwala.com" ||
  "https://kaamwala-server.vercel.app" ||
  "http://localhost:5050";
export const API_URL = `${SERVER_URL}/api/v1`;

export const TOKEN_KEY = "kw_token";
export const USER_KEY = "kw_user";

/** localStorage throws in private mode on some browsers - never let that crash a render. */
export const storage = {
  get: (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* quota / private mode */
    }
  },
  remove: (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  },
};

const client = axios.create({
  baseURL: API_URL,
  timeout: 25000,
  headers: { "Content-Type": "application/json" },
});

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

client.interceptors.request.use((config) => {
  const token = storage.get(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res.data,
  (error) => {
    const status = error?.response?.status;
    const message =
      error?.response?.data?.message ||
      (error.code === "ECONNABORTED"
        ? "The request timed out. Check your connection."
        : null) ||
      (!error.response
        ? "Cannot reach the server. Is it running?"
        : "Something went wrong");

    if (status === 401) {
      storage.remove(TOKEN_KEY);
      storage.remove(USER_KEY);
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(
      Object.assign(new Error(message), {
        status,
        errors: error?.response?.data?.errors,
      })
    );
  }
);

/** Turn a stored path into an absolute URL. */
export const fileUrl = (value) => {
  if (!value) return null;
  if (String(value).startsWith("http")) return value;
  return `${SERVER_URL}${String(value).startsWith("/") ? "" : "/"}${value}`;
};

/**
 * `folder` tells the server which bucket folder the file belongs in - logo,
 * cover, gallery, menu, product, service, document, avatar. Unknown values fall
 * back to "misc" server-side, so an upload can never fail over it.
 */
export const uploadFiles = async (files = [], folder = "misc") => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append("files", file, file.name));
  const res = await client.post(
    `/upload/multiple?folder=${encodeURIComponent(folder)}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return (res.data || []).map((f) => f.url);
};

export const uploadFile = async (file, folder = "misc") => {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await client.post(
    `/upload?folder=${encodeURIComponent(folder)}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data;
};

/**
 * Uploads a file straight to S3 with a signed URL, skipping our API.
 *
 * Greeting videos have no size limit, so they must not pass through the
 * server: it would buffer the whole file in memory, and a serverless host caps
 * the request body at a few megabytes anyway. The server only signs a PUT for
 * one exact key; we store the resulting public URL.
 *
 * `onProgress` receives 0-1 so the composer can show a real bar.
 */
export const uploadDirect = (file, folder = "misc", onProgress) =>
  new Promise((resolve, reject) => {
    (async () => {
      const { data } = await client.post("/upload/sign", {
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        folder,
      });
      // XHR rather than fetch: it is the only one that reports upload progress.
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", data.uploadUrl);
      // Exactly the headers the signature covers - S3 rejects the PUT otherwise.
      Object.entries(
        data.headers || { "Content-Type": data.contentType }
      ).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve(data.url)
          : reject(new Error(`Upload failed (${xhr.status})`));
      xhr.onerror = () =>
        reject(new Error("Upload failed. Check your connection."));
      xhr.send(file);
    })().catch(reject);
  });

export default client;
